import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Tooltip,
} from '@mui/material';
import { Refresh, HelpOutline, Quiz, Person, Storefront, ExpandMore, Edit, Delete, Add } from '@mui/icons-material';
import { getFaqs, deleteFaq } from '../../services/Api';
import { useNavigate } from 'react-router-dom';
import DeleteConfirm from '../../modals/DeleteConfirm';

const GetAllFqas = () => {
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [type, setType] = useState('vendor');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [faqToDelete, setFaqToDelete] = useState(null);
    const navigate = useNavigate();

    const fetchFaqs = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getFaqs(type);
            if (response.success) {
                setFaqs(response.data);
            } else {
                setError(response?.message || 'Failed to fetch FAQs');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching FAQs');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [type]);

    useEffect(() => {
        fetchFaqs();
    }, [type, fetchFaqs]);

    const handleDeleteClick = (e, id) => {
        e.stopPropagation();
        setFaqToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleEditClick = (e, faq) => {
        e.stopPropagation();
        navigate(`/faqs/modify/${faq.id}`, { state: { faq } });
    };

    const handleConfirmDelete = async () => {
        if (!faqToDelete) return;
        try {
            setLoading(true);
            const response = await deleteFaq(faqToDelete);
            if (response.success) {
                setDeleteDialogOpen(false);
                setFaqToDelete(null);
                fetchFaqs();
            } else {
                console.error('Failed to delete FAQ');
            }
        } catch (err) {
            console.error('An error occurred while deleting the FAQ:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{}}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 300, textTransform: 'capitalize' }}>
                        Frequently Asked Questions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage common questions and answers for your {type}s
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl sx={{ minWidth: 160 }} size="small">
                        <InputLabel id="type-select-label">Account Type</InputLabel>
                        <Select
                            labelId="type-select-label"
                            id="type-select"
                            value={type}
                            label="Account Type"
                            onChange={(e) => setType(e.target.value)}
                            sx={{ backgroundColor: 'white' }}
                        >
                            <MenuItem value="vendor">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Storefront fontSize="small" /> Vendor
                                </Box>
                            </MenuItem>
                            <MenuItem value="user">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Person fontSize="small" /> User
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <IconButton onClick={fetchFaqs} disabled={loading} sx={{ backgroundColor: 'rgba(120, 81, 169, 0.1)', color: 'primary.main' }}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate('/faqs/modify')}
                        sx={{
                            backgroundColor: 'primary.main',
                            '&:hover': { backgroundColor: 'primary.dark' },
                            px: 3,
                        }}
                    >
                        Add FAQ
                    </Button>
                </Box>
            </Box>

            {/* Content Section */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress color="primary" />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 4 }}>
                    {error}
                </Alert>
            ) : (faqs?.faqs?.length === 0 || !faqs?.faqs) ? (
                <Paper
                    sx={{
                        p: 8,
                        textAlign: 'center',
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        border: '2px dashed',
                        borderColor: 'text.border',
                        borderRadius: 4,
                    }}
                >
                    <HelpOutline sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No FAQs found for {type}s
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/faqs/modify')}
                        sx={{ mt: 2 }}
                    >
                        Create your first FAQ
                    </Button>
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {faqs?.faqs?.map((faq) => (
                        <Accordion
                            key={faq.id}
                            elevation={0}
                            sx={{
                                border: '1px solid',
                                borderColor: 'text.border',
                                boxShadow: 'none',
                                borderRadius: '12px !important',
                                '&:before': { display: 'none' },
                                '&:hover': { borderColor: 'primary.main' },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                sx={{
                                    px: 3,
                                    py: 1,
                                    '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2, justifyContent: 'space-between' }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Quiz color="primary" sx={{ fontSize: 22, opacity: 0.8 }} />
                                    <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>{faq.question}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', mr: 2 }}>
                                    <Tooltip title="Edit FAQ">
                                        <IconButton component="span" size="small" color="primary" onClick={(e) => handleEditClick(e, faq)} sx={{ mr: 1 }}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete FAQ">
                                        <IconButton component="span" size="small" color="error" onClick={(e) => handleDeleteClick(e, faq.id)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 3, pb: 3, pt: 1, color: 'text.secondary', borderTop: '1px solid #f0f0f0' }}>
                                <Typography
                                    component="div"
                                    variant="body1"
                                    sx={{
                                        lineHeight: 1.7,
                                        color: 'text.primary',
                                        opacity: 0.8,
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        '& p': { mb: 1 },
                                        '& ul, & ol': { pl: 3, mb: 1 },
                                        '& img': { maxWidth: '100%', height: 'auto' }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: faq.answer }}
                                />
                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    {/* <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Paper elevation={0} sx={{ px: 1, py: 0.2, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, borderRadius: 1, backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid', borderColor: 'text.border' }}>
                                            {faq.type || type}
                                        </Paper>
                                    </Box> */}
                                    {/* <Typography variant="caption" color="text.disabled">
                                        Last Updated: {new Date().toLocaleDateString()} • ID: {faq.id}
                                    </Typography> */}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}

            <DeleteConfirm
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete FAQ?"
                message="Are you sure you want to delete this FAQ? This action will remove it from the mobile app immediately."
            />
        </Box>
    );
};

export default GetAllFqas;
